import { useState, useEffect, useCallback } from 'react';
import { signInWithPopup, GoogleAuthProvider, signOut, onAuthStateChanged } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { auth, db } from '../firebase/config';
import { useToast } from '../components/Toast';

/**
 * Custom hook for Firebase authentication with admin verification.
 * Handles Google OAuth login, admin status verification, and logout.
 */
export function useAuth() {
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [user, setUser] = useState(null);
    const [authLoading, setAuthLoading] = useState(true);
    const { showToast } = useToast();

    useEffect(() => {
        let isMounted = true;

        const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
            if (!isMounted) return;

            setAuthLoading(true);
            if (currentUser) {
                // Check if user is admin in Firestore
                // Note: Google OAuth emails are case-insensitive, but Firestore document IDs are case-sensitive
                // We check both the original email and lowercase version
                try {
                    const userEmail = currentUser.email;
                    const userEmailLower = userEmail?.toLowerCase();

                    // Try original case first, then lowercase
                    let adminRef = doc(db, 'admins', userEmail);
                    let adminSnap = await getDoc(adminRef);

                    // If not found, try lowercase version
                    if (!adminSnap.exists() && userEmailLower && userEmailLower !== userEmail) {
                        adminRef = doc(db, 'admins', userEmailLower);
                        adminSnap = await getDoc(adminRef);
                    }

                    if (!isMounted) return;

                    if (adminSnap.exists()) {
                        setIsAuthenticated(true);
                        setUser(currentUser);
                    } else {
                        // Check for temporary bootstrapping or specific allowed email if firestore is empty
                        // For now, stricter security: strict firestore check
                        if (import.meta.env.DEV) {
                            console.warn('User not found in admins collection:', userEmail);
                            console.warn('Tried both:', userEmail, 'and', userEmailLower);
                        }
                        showToast('Access denied. You are not an authorized admin.', 'error');
                        await signOut(auth);
                        if (isMounted) {
                            setIsAuthenticated(false);
                            setUser(null);
                        }
                    }
                } catch (error) {
                    if (import.meta.env.DEV) {
                        console.error('Error verifying admin status:', error);
                    }
                    if (isMounted) {
                        showToast('Error verifying admin status', 'error');
                        setIsAuthenticated(false);
                    }
                }
            } else {
                if (isMounted) {
                    setIsAuthenticated(false);
                    setUser(null);
                }
            }
            if (isMounted) {
                setAuthLoading(false);
            }
        });

        return () => {
            isMounted = false;
            unsubscribe();
        };
    }, [showToast]);

    const handleLogin = useCallback(async () => {
        try {
            const provider = new GoogleAuthProvider();
            await signInWithPopup(auth, provider);
            // The onAuthStateChanged listener will handle the rest
        } catch (error) {
            if (import.meta.env.DEV) {
                console.error('Login error:', error);
            }
            showToast('Login failed: ' + (error?.message || 'Unknown error'), 'error');
        }
    }, [showToast]);

    const handleLogout = useCallback(async (onLogoutCallback) => {
        try {
            await signOut(auth);
            setIsAuthenticated(false);
            if (onLogoutCallback) onLogoutCallback();
            showToast('Logged out successfully', 'success');
        } catch (error) {
            showToast('Logout failed', 'error');
        }
    }, [showToast]);

    return {
        isAuthenticated,
        user,
        authLoading,
        handleLogin,
        handleLogout
    };
}

export default useAuth;
